import {CommonModule} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  viewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {Image} from '@sportngin/se-commerce-sdk/models/clare';
import {merge, Observable, Subject, takeUntil} from 'rxjs';
import {map, withLatestFrom} from 'rxjs/operators';
import {SeFeAlertModule} from 'se-fe-alert';
import {SeFeApiModule} from 'se-fe-api';
import {SeFeAttachmentServiceModule} from 'se-fe-attachment-service';
import {SeFeButtonModule} from 'se-fe-button';
import {SeFeCardModule} from 'se-fe-card';
import {SeFeChipModule} from 'se-fe-chip';
import {SeFeDomainEnvironmentModule} from 'se-fe-domain-environment';
import {SeFeFormFieldConfig, SeFeFormFieldModule} from 'se-fe-form-field';
import {
  SeFeFormFieldMultiselectComponent,
  SeFeFormFieldMultiselectModule,
  SeFeMultiselectOptions,
} from 'se-fe-form-field-multiselect';
import {SeFeFormFieldOptionsModule} from 'se-fe-form-field-options';
import {SeFeFormFieldTextModule} from 'se-fe-form-field-text';
import {SeFeFormServiceModule} from 'se-fe-form-service';
import {SeFeGridModule} from 'se-fe-grid';
import {SeFeHeaderModule} from 'se-fe-header';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeOverlayModule} from 'se-fe-overlay';
import {SeFePageModule} from 'se-fe-page';
import {SeFeTestIdModule} from 'se-fe-test-id';
import {SeFeTooltipComponent, SeFeTooltipModule} from 'se-fe-tooltip';
import {SeFeWysiwygModule} from 'se-fe-wysiwyg';
import {MultiImageUploadFieldComponent} from '../../../../common/components/multi-image-upload-field/multi-image-upload-field.component';
import {FullPageSpinnerComponent} from '../../../../common/components/full-page-spinner/full-page-spinner.component';
import {MAX_ALLOWED_VARIANTS} from '../../../../common/constants';
import {ProductManagementErrorInterface} from '../../../../error/error-handling/product-management-error';
import {CatalogSummary, getProductId, Product, ProductSpec} from '../../../../models/clare';
import {AnalyticsEvents, AnalyticsService} from '../../../../services/analytics.service';
import {selectOrganizationName} from '../../../../state/organization/organization.selectors';
import {selectSelectedOrganizationId} from '../../../../state/router/router.selectors';
import {trimFormValue} from '../../../../utils/form-utils';
import * as ImageFormUtils from '../../../../utils/image-form-utils';
import {ProductFormValue, SpecOptionPayload} from './create-product.types';
import {CreateProductActions} from './store/create-product.actions';
import {
  selectAddAnotherProduct,
  selectCreateProductCatalogs,
  selectCreateProductCatalogsLoading,
  selectCreateProductError,
  selectCreateProductLoading,
  selectCreateProductRequest,
} from './store/create-product.selectors';

const validateVariantCount = (): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const sizes = control.get('productSizesSpec')?.value || [];
    const colors = control.get('productColorsSpec')?.value || [];
    const totalVariants = (sizes.length || 1) * (colors.length || 1);

    if (totalVariants > MAX_ALLOWED_VARIANTS) {
      return {variantCountExceeded: true};
    }

    return null;
  };
};

let createProductRequestSequence = 0;

const nextCreateProductRequestId = (): string => `create-product-${++createProductRequestSequence}`;

@Component({
  selector: 'create-product',
  imports: [
    CommonModule,
    FullPageSpinnerComponent,
    MultiImageUploadFieldComponent,
    ReactiveFormsModule,
    SeFeAlertModule,
    SeFeApiModule,
    SeFeAttachmentServiceModule,
    SeFeButtonModule,
    SeFeCardModule,
    SeFeChipModule,
    SeFeDomainEnvironmentModule,
    SeFeFormFieldModule,
    SeFeFormFieldMultiselectModule,
    SeFeFormFieldOptionsModule,
    SeFeFormFieldTextModule,
    SeFeFormServiceModule,
    SeFeGridModule,
    SeFeHeaderModule,
    SeFeIconModule,
    SeFeOverlayModule,
    SeFePageModule,
    SeFeTestIdModule,
    SeFeTooltipModule,
    SeFeWysiwygModule,
  ],
  templateUrl: './create-product.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-product.component.scss',
})
export class CreateProductComponent implements OnInit, OnDestroy {
  @ViewChild(MultiImageUploadFieldComponent) multiImageUploadField?: MultiImageUploadFieldComponent;

  selectControls = viewChildren(SeFeFormFieldMultiselectComponent);

  pageOptionsForm = new FormGroup({
    addAnotherProduct: new FormControl(false, {nonNullable: true}),
  });

  addAnotherProductOptions = [{label: 'Add another product', value: true}];
  savePending = signal(false);
  renderFormContent = false;
  renderDescriptionEditor = false;
  ready = false;

  readonly productImageAltValidators = [Validators.required, Validators.maxLength(200)];
  readonly MAX_ALLOWED_VARIANTS = MAX_ALLOWED_VARIANTS;

  productNameConfig: SeFeFormFieldConfig = {
    label: 'Name',
    required: true,
    errorMessages: {
      required: 'Please enter a product name',
      maxlength: 'Name exceeds maximum length of 100 characters.',
    },
  };

  productPriceConfig: SeFeFormFieldConfig = {
    label: 'Price',
    required: true,
    errorMessages: {
      pattern: 'Please enter a valid price (e.g., $25 or $0.99)',
      required: 'Please enter a price',
      min: 'Price cannot be less than 0',
      max: 'Value exceeds the limit of $1,000,000',
    },
  };

  productDescLabel: SeFeFormFieldConfig = {
    label: 'Description',
    required: false,
  };

  productImageLabel: SeFeFormFieldConfig = {
    label: 'Product Images',
    required: false,
  };

  productColorLabel: SeFeFormFieldConfig = {
    label: 'Colors',
    required: false,
  };

  productSizeLabel: SeFeFormFieldConfig = {
    label: 'Sizes',
    required: false,
  };

  brandLabel: SeFeFormFieldConfig = {
    label: 'Brand',
    errorMessages: {
      maxlength: 'Brand name exceeds the maximum length of 100 characters',
    },
  };

  specsValidationConfig: SeFeFormFieldConfig = {
    label: '',
    errorMessages: {
      maxlength: 'Value exceeds maximum length',
    },
  };

  taxCodeLabel: SeFeFormFieldConfig = {
    label: 'Tax Code',
    errorMessages: {
      maxlength: 'Tax code exceeds maximum length of 25 characters',
    },
  };

  productCatalogsLabel: SeFeFormFieldConfig = {
    label: 'Catalogs',
    required: false,
  };

  specsMaxLengthValidator = Validators.maxLength(100);

  colors = {
    sections: [
      {
        options: [
          {text: 'Black', value: 'Black'},
          {text: 'Gray', value: 'Gray'},
          {text: 'White', value: 'White'},
        ],
      },
    ],
  };

  sizes = {
    sections: [
      {
        options: [
          {text: 'Small', value: 'Small'},
          {text: 'Medium', value: 'Medium'},
          {text: 'Large', value: 'Large'},
        ],
      },
    ],
  };

  error$: Observable<ProductManagementErrorInterface | undefined>;
  loading$: Observable<boolean>;
  loading: Signal<boolean>;
  catalogs: Signal<CatalogSummary[]>;
  catalogsLoading: Signal<boolean>;
  catalogOptions: Signal<SeFeMultiselectOptions>;
  orgId: Signal<string | undefined>;
  orgName: Signal<string | undefined>;
  productListBackLink: Signal<string> = computed(() => {
    const orgId = this.orgId();
    return orgId ? `/org/${orgId}/commerce/products` : '/';
  });
  saveButtonDisabled: Signal<boolean> = computed(() => {
    this.formValue();
    return this.loading() || this.savePending() || !this.productNameControl.value.trim();
  });
  /**
   * Cancel stays enabled for form discard, but is locked while an in-flight create is running so Cancel does not imply stopping backend work mid-save.
   */
  cancelButtonDisabled: Signal<boolean> = computed(() => this.loading() || this.savePending());

  productForm: FormGroup;
  readonly colorsReadonly: Signal<boolean>;
  readonly sizesReadonly: Signal<boolean>;

  private readonly onDestroy = new Subject<void>();
  private readonly formValue: Signal<ProductFormValue | undefined>;
  private currentCreateProductRequestId: string | undefined;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private router: Router,
    private analytics: AnalyticsService
  ) {
    this.createForm();
    this.loading$ = this.store.select(selectCreateProductLoading);
    this.loading = toSignal(this.loading$, {initialValue: false});
    this.error$ = this.store.select(selectCreateProductError);
    this.catalogs = toSignal(this.store.select(selectCreateProductCatalogs), {requireSync: true});
    this.catalogsLoading = toSignal(this.store.select(selectCreateProductCatalogsLoading), {requireSync: true});
    this.catalogOptions = computed<SeFeMultiselectOptions>(() => ({
      sections: [
        {
          options: this.catalogs().map(catalog => ({text: catalog.name, value: String(catalog.id)})),
        },
      ],
    }));
    this.orgId = toSignal(this.store.select(selectSelectedOrganizationId), {requireSync: true});
    this.orgName = toSignal(this.store.select(selectOrganizationName), {requireSync: true});

    const formChanges$ = merge(this.productForm.valueChanges, this.productForm.statusChanges).pipe(
      map(() => this.productForm.getRawValue())
    );

    this.formValue = toSignal(formChanges$, {initialValue: this.productForm.getRawValue()});
    this.colorsReadonly = computed(() => {
      const formVal = this.formValue();
      const colorsCount = formVal?.productColorsSpec?.length ?? 0;
      const sizesCount = formVal?.productSizesSpec?.length ?? 0;
      const colorsPlus1 = (colorsCount + 1) * (sizesCount || 1);
      return colorsPlus1 > MAX_ALLOWED_VARIANTS;
    });
    this.sizesReadonly = computed(() => {
      const formVal = this.formValue();
      const colorsCount = formVal?.productColorsSpec?.length ?? 0;
      const sizesCount = formVal?.productSizesSpec?.length ?? 0;
      const sizesPlus1 = (colorsCount || 1) * (sizesCount + 1);
      return sizesPlus1 > MAX_ALLOWED_VARIANTS;
    });
  }

  get descriptionControl(): FormControl<string> {
    return this.productForm.get('description') as FormControl;
  }

  get productNameControl(): FormControl<string> {
    return this.productForm.get('productName') as FormControl;
  }

  get imageDescriptionControl(): FormControl<string> {
    return this.productForm.get('imageDescription') as FormControl;
  }

  get taxCodeControl(): FormControl<string> {
    return this.productForm.get('taxCode') as FormControl;
  }

  get productImagesFormArray(): FormArray<ImageFormUtils.ImageFormGroup> {
    return this.productForm.get('images') as FormArray<ImageFormUtils.ImageFormGroup>;
  }

  get productImages(): Image[] {
    return ImageFormUtils.mapImageFormArrayToImages(this.productImagesFormArray);
  }

  set productImages(images: Image[]) {
    ImageFormUtils.replaceImageFormArray(
      this.productImagesFormArray,
      images,
      this.createProductImageFormGroup.bind(this)
    );
    this.productImagesFormArray.updateValueAndValidity({emitEvent: false});
    this.productForm.updateValueAndValidity({emitEvent: false});
  }

  ngOnInit(): void {
    this.store.dispatch(CreateProductActions.loadCatalogs());
    this.renderFormContent = true;
    this.showDescriptionEditorWhenReady();
    this.store
      .select(selectCreateProductRequest)
      .pipe(withLatestFrom(this.store.select(selectAddAnotherProduct)), takeUntil(this.onDestroy))
      .subscribe(([request, addAnotherProduct]) => {
        if (!this.currentCreateProductRequestId || request.requestId !== this.currentCreateProductRequestId) return;
        if (request.status === 'idle' || request.status === 'pending') return;

        this.savePending.set(false);
        this.currentCreateProductRequestId = undefined;
        if (request.status === 'failure' || addAnotherProduct || !request.product) return;

        const orgId = this.orgId();
        const productId = getProductId(request.product);
        if (orgId && productId) {
          this.router.navigate(['/org', orgId, 'commerce', 'products', productId], {
            replaceUrl: true,
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.abandonCreateProductRequest();
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  handleSave(): void {
    if (this.saveButtonDisabled()) {
      return;
    }

    this.handleAdd(this.pageOptionsForm.controls.addAnotherProduct.value);
  }

  handleAdd(addAnotherProduct: boolean): void {
    trimFormValue(this.productForm, 'taxCode');
    trimFormValue(this.productForm, 'productName');
    trimFormValue(this.productForm, 'imageDescription');
    trimFormValue(this.productForm, 'brand');
    trimFormValue(this.productForm, 'description');
    this.trimProductImageDescriptions();
    this.markFormGroupControls(this.productForm);
    this.cdr.markForCheck();

    if (!this.productForm.valid) {
      return;
    }

    const requestId = nextCreateProductRequestId();
    this.currentCreateProductRequestId = requestId;
    this.savePending.set(true);
    const values: ProductFormValue = this.productForm.value;
    const product = this.generateProductPayload(values);
    const specs = this.generateSpecPayload(values);
    const specOptions = this.generateOptionsPayload(values);

    this.store.dispatch(CreateProductActions.createAnotherProduct({addAnotherProduct}));
    this.store.dispatch(
      CreateProductActions.createProduct({
        data: {product, specs, specOptions, taxCode: values.taxCode},
        requestId,
      })
    );

    if (addAnotherProduct) {
      this.resetForm(true);
    }
  }

  onCancel(): void {
    if (!this.cancelButtonDisabled()) {
      this.abandonCreateProductRequest();
      this.analytics.trackAction(AnalyticsEvents.CancelAddProduct, {event_type: 1});

      const orgId = this.orgId();
      if (orgId) {
        this.router.navigate(['/org', orgId, 'commerce', 'products']);
      }
    }
  }

  generateProductPayload(values: ProductFormValue): Product {
    const product: Product = {
      name: values.productName.trim(),
      active: false,
      only_sold_in_bundles: false,
      description: values.description || undefined,
      price_cents: values.productPrice * 100,
      catalog_ids: values.catalogIds.map(catalogId => Number(catalogId)),
      display_metadata: {
        template_key: 'yso-products',
        title: values.productName.trim(),
        gender: 'unisex',
        images: [],
        fulfilled_by: this.orgName(),
        brand: values.brand || undefined,
      },
    };
    const defaultAlt = (values.imageDescription ?? '').trim() || values.productName.trim();
    for (const img of this.productImages) {
      const src = img.src?.trim();
      if (src) {
        product.display_metadata!.images!.push({src, alt: img.alt?.trim() || defaultAlt});
      }
    }
    return product;
  }

  generateSpecPayload(values: ProductFormValue): ProductSpec[] {
    return this.getSpecProperties(values).map(key => ({name: this.generateSpecName(key)}));
  }

  generateOptionsPayload(values: ProductFormValue): SpecOptionPayload {
    return this.getSpecProperties(values).reduce((acc: SpecOptionPayload, key) => {
      const specName = this.generateSpecName(key);
      const control: SeFeFormFieldMultiselectComponent | undefined = this.selectControls().find(
        select => select.elementRef.nativeElement.id === key
      );
      const formControlValue: string[] = this.productForm.value[key];
      const newOptions = control?.selectedOptions
        .filter(item => formControlValue.includes(item.value))
        .map(item => ({name: item.text}));

      acc[specName] = newOptions ?? [];
      return acc;
    }, {});
  }

  getSpecProperties(values: ProductFormValue): string[] {
    return Object.keys(values)
      .filter(k => k.toLowerCase().includes('spec'))
      .filter(key => {
        const value = values[key as keyof ProductFormValue] as Array<string>;
        return value.length !== 0;
      });
  }

  generateSpecName(specLabel: string): string {
    return specLabel.replace(/product|Spec/g, '');
  }

  onProductImagesManualClear(): void {
    this.imageDescriptionControl.removeValidators([Validators.required]);
    this.imageDescriptionControl.setValue('', {emitEvent: false});
    this.imageDescriptionControl.updateValueAndValidity({emitEvent: false});
    this.cdr.markForCheck();
  }

  formHasErrors(): boolean {
    return !!this.productForm.errors?.['required'];
  }

  markFormGroupControls(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupControls(control);
      } else {
        control.markAsTouched({onlySelf: true});
        control.markAsDirty({onlySelf: true});
      }
    });
  }

  resetForm(keepFormVisible = false): void {
    if (!keepFormVisible) {
      this.renderFormContent = false;
      this.renderDescriptionEditor = false;
      this.ready = false;
    }
    this.productImagesFormArray.clear();
    this.productForm.reset({
      productName: '',
      productPrice: '',
      productColorsSpec: [],
      productSizesSpec: [],
      imageDescription: '',
      images: [],
      description: '',
      brand: '',
      taxCode: '',
      catalogIds: [],
    });
    this.productForm.markAsUntouched();
    this.productForm.markAsPristine();

    this.multiImageUploadField?.resetUploadAlerts();
    this.productImagesFormArray.updateValueAndValidity({emitEvent: false});
    this.productForm.updateValueAndValidity({emitEvent: false});
    this.ready = true;
  }

  getColorsTooltip(tooltip: SeFeTooltipComponent): SeFeTooltipComponent | null {
    return this.colorsReadonly() ? tooltip : null;
  }

  getSizesTooltip(tooltip: SeFeTooltipComponent): SeFeTooltipComponent | null {
    return this.sizesReadonly() ? tooltip : null;
  }

  private showDescriptionEditorWhenReady(): void {
    this.renderDescriptionEditor = false;
    setTimeout(() => {
      this.renderDescriptionEditor = true;
    });
  }

  private createForm(): void {
    this.productForm = this.fb.group({
      productName: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(100)],
        nonNullable: true,
      }),
      productPrice: new FormControl('', {
        validators: [
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
          Validators.min(0),
          Validators.max(1_000_000),
        ],
        nonNullable: true,
      }),
      productColorsSpec: [[]],
      productSizesSpec: [[]],
      imageDescription: new FormControl('', [Validators.maxLength(200)]),
      images: new FormArray<ImageFormUtils.ImageFormGroup>([]),
      description: ['', [Validators.maxLength(2000)]],
      brand: ['', [Validators.maxLength(100)]],
      taxCode: ['', [Validators.maxLength(25)]],
      catalogIds: new FormControl<string[]>([], {nonNullable: true}),
    });
    this.productForm.addValidators(validateVariantCount());
    this.ready = true;
  }

  private createProductImageFormGroup(src = '', alt = ''): ImageFormUtils.ImageFormGroup {
    return ImageFormUtils.createImageFormGroup(src, alt, [Validators.required, Validators.maxLength(200)]);
  }

  private abandonCreateProductRequest(): void {
    const requestId = this.currentCreateProductRequestId;
    this.currentCreateProductRequestId = undefined;
    this.savePending.set(false);
    if (requestId) {
      this.store.dispatch(CreateProductActions.abandonCreateProductRequest({requestId}));
    }
  }

  private trimProductImageDescriptions(): void {
    ImageFormUtils.trimImageAltValues(this.productImagesFormArray);
  }
}
